<script setup lang="ts">
import {ref, computed, watch, onUpdated, onActivated, nextTick, onDeactivated} from 'vue';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { ClientFeatureType, GeneralPurposeVariant } from 'src/constants/enums';
import { useDashboardStore, useGeneralPurposeModalStore, useMessagesStore, useSessionStore } from 'src/store';
import { reformatString } from 'src/utils/stringUtils';
import MessageDisplay from 'src/pages/DashboardMessages/components/MessageDisplay.vue';
import { getMessages, markAllMessagesAsRead, sendMessage } from 'src/api/endpoints/messagesWeb';
import DisclaimerModal from 'src/components/DisclaimerModal.vue';
import TheYourCareTeam from 'src/pages/DashboardMessages/components/TheYourCareTeam.vue'
import type {GeneralPurposeModalModel, MessageModel} from '@/types/interfaces';
import { useQuasar, QScrollArea } from 'quasar';
import { useRoute, useRouter } from 'vue-router';
import { bus } from '@/utils/eventBus';

import { useRealtimeStore } from 'src/store/realtimeModule';
import { storeToRefs } from 'pinia';
import {useI18n} from 'vue-i18n';
const {t} = useI18n();

const route = useRoute();
const router = useRouter();
const newMessage = ref('');
const showCareTeamModal = ref(false);
const showMessagesDisclaimer = ref(false);
const disclaimerRef = ref<InstanceType<typeof DisclaimerModal> | null>(null);

const messagesModule = useMessagesStore();
const sessionModule = useSessionStore();
const dashboardStore = useDashboardStore();
const generalPurposeModalStore = useGeneralPurposeModalStore();
const $q = useQuasar()


const currentUserId = computed(() => sessionModule.user?.userId);
const eocId = computed(() => sessionModule.user?.selectedEocId);
const isLoading = computed(() => messagesModule.isLoading);
const messages = computed(() => messagesModule.allMessages);
const todaysDate = computed(() => dashboardStore.todaysDate);
const maEnabled = computed(() => dashboardStore?.patient?.messageAssistantEnabled);
const maPollInterval = computed(() => dashboardStore?.patient?.messageAssistantPollingInterval);

const messagesDisabled = computed(() => !sessionModule.user?.clientFeatures.includes(ClientFeatureType.Messaging));
const hasMAOnboardingInterrupt = computed(() =>
  !!dashboardStore.patient?.interruptData?.interrupts?.find(
    (i: any) => i.type == 'MA_Onboarding'
  )
);
const hideMessagesInput = computed(() => messagesDisabled.value || !messages.value || messages.value.length <= 0);
const noMessages = computed(() => reformatString(messagesDisabled.value ? t('messages_disabled') : t('messages_disabled_none')));
const threadScrollArea = ref(null as QScrollArea | null);

const outOfOfficeMessageDescription = computed(() => messagesModule.outOfOfficeMessageDescription);
const outOfOfficeMessageTitle = computed(() => messagesModule.outOfOfficeMessageTitle);
const oooMessage = ref();
const oooMsgHeight = ref(0);

const scrollToThreadBottomPending = ref(true);

const setOooMsgMore = computed(() => {
  return oooMsgHeight.value > 48;
});

let maPoll: number | null = null;
const realtime = useRealtimeStore();
const { isConnected } = storeToRefs(realtime);
// newMessage is now a GLOBAL handler in TheDashboard.vue — keeps the messages
// store fresh anywhere in the dashboard, not just on this page. We react to
// store changes via the watch below instead of subscribing here.

function isAIMsg (message: MessageModel): boolean {
  const test = /assistant/i;
  return message.type.match(test) !== null ? true : false;
}

watch(hideMessagesInput, async (newVal) => {
  if (!newVal) {
    await nextTick(() => {
      scrollToThreadBottomPending.value = true;
      if (route.name === 'Dashboard.Messages') {
        scrollToThreadBottom();
      }
    });
  }
});

// Disclaimer modal closes → user clicked Continue
watch(showMessagesDisclaimer, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    checkAndNavigateToOnboarding();
  }
});

// API confirms disclaimer already dismissed (first mount, modal never shown)
watch(
  () => disclaimerRef.value?.isDismissed,
  (isDismissed) => {
    if (isDismissed && !showMessagesDisclaimer.value) {
      checkAndNavigateToOnboarding();
    }
  }
);

onActivated(async () => {
  // Scroll on first ever activation OR when the patient returns to Messages
  // with stranded unread messages. The unread-count guard catches the case
  // where a `newMessage` event arrived while the patient was on another tab:
  // the global handler in TheDashboard.vue updated the store, but
  // markMessagesAsRead never fired (because the patient wasn't viewing the
  // tab). Scrolling here calls markMessagesAsRead inside, which clears the
  // badge.
  if (scrollToThreadBottomPending.value || messagesModule.unreadMessagesCount > 0) {
    setOutOfOfficeModal();
    await nextTick();
    scrollToThreadBottom();
  }

  // MA polling fallback only when the realtime connection failed to establish.
  // The watch on messages.length below handles the scroll when new messages
  // arrive via the poll.
  if (!isConnected.value && maEnabled.value) {
    maPoll = setInterval(reloadMessages, maPollInterval.value * 1000);
  }

  // On keep-alive reactivation: if disclaimer already resolved, check onboarding
  if (disclaimerRef.value?.isDismissed && !showMessagesDisclaimer.value) {
    checkAndNavigateToOnboarding();
  }
});

onDeactivated(()=>{
  if (maPoll) {
    clearInterval(maPoll);
    maPoll = null;
  }
})

// When new messages arrive in the store from any source — the global
// newMessage handler in TheDashboard.vue, the MA poll fallback, attemptSend's
// reload, the on-bot-resolved bus event — scroll to bottom if the patient is
// currently viewing the Messages tab. Route guard prevents marking messages
// as read when the patient hasn't actually seen them; in that case the
// unread-count guard in onActivated handles it on their next visit.
watch(() => messagesModule.messages.length, async (newLen, oldLen) => {
  if (newLen > oldLen && route.name === 'Dashboard.Messages') {
    await nextTick();
    scrollToThreadBottom(300);
  }
});

onUpdated(() => {
  oooMsgHeight.value = oooMessage.value?.clientHeight;
});

function checkAndNavigateToOnboarding() {
  const tryNavigate = () => {
    const onboardingInterrupt = dashboardStore.patient?.interruptData?.interrupts?.find(
      (i: any) => i.type == 'MA_Onboarding'
    );
    if (onboardingInterrupt) {
      router.replace({
        name: 'MAOnboarding',
        params: { surveyId: onboardingInterrupt.surveyId, redirectRoute: 'Dashboard.Messages' }
      });
      return true;
    }
    return false;
  };

  if (tryNavigate()) return;

  // If patient data hasn't loaded yet, watch for it
  if (!dashboardStore.patient) {
    const stopWatch = watch(() => dashboardStore.patient, (newVal) => {
      if (newVal) {
        tryNavigate();
        stopWatch();
      }
    });
  }
}


async function reloadMessages() {
  const messagesResponse = await handleApiRequest(() => getMessages(), null, $q);
  if (messagesResponse) {
    messagesModule.loadMessages(messagesResponse.result);
    // Awaited so clearUnreadMessagesCount() runs before reloadMessages returns.
    // Subsequent markMessagesAsRead() calls (e.g., from scrollToThreadBottom)
    // then short-circuit at the unreadMessagesCount==0 guard, preventing a
    // duplicate MarkAllMessagesAsRead HTTP call.
    await markMessagesAsRead();
  }
}

async function markMessagesAsRead() {
  if (messagesModule.unreadMessagesCount == 0) return;
  const result = await handleApiRequest(() => markAllMessagesAsRead(eocId.value), null, $q);
  if (result?.result) {
    messagesModule.clearUnreadMessagesCount();
  }
}

async function attemptSend(clientId: string, model: { surgeonId: number; careTeamId: number; dataId: null; message: string; eocId: number | undefined; senderConnectionId: string | null }) {
  try {
    const response = await sendMessage(model);
    if (response.data?.result) {
      // Server no longer echoes the SignalR newMessage event back to the sender,
      // so this post-send reload is the ONLY path that clears the pending message.
      // Use try/finally so a reload failure can't strand the message at 50% opacity.
      try {
        await reloadMessages();
      } finally {
        messagesModule.removePendingMessage(clientId);
      }
    } else {
      messagesModule.updatePendingMessageStatus(clientId, 'failed');
    }
  } catch {
    messagesModule.updatePendingMessageStatus(clientId, 'failed');
  }
  await nextTick();
  scrollToThreadBottom(300);
}

async function onSendClick() {
  if (!newMessage.value) return;

  const clientId = `pending_${Date.now()}`;
  const pendingMsg: MessageModel = {
    messageId: -1,
    sortIndex: null,
    message: newMessage.value,
    readDate: null,
    createdDate: new Date().toISOString(),
    dataId: '',
    toId: 0,
    fromId: Number(currentUserId.value) || 0,
    mediaUrl: '',
    assistantConversationId: null,
    isResponseHelpful: null,
    helpfulDate: null,
    resolution: null,
    type: 'Standard',
    mediaContentType: '',
    conversationGuid: '',
    sources: [],
    externalSources: [],
    _status: 'pending',
    _clientId: clientId,
  };

  const createMessageRequestModel = {
    surgeonId: dashboardStore.patient?.surgeonId || 0,
    careTeamId: dashboardStore.patient?.careTeamId || 0,
    dataId: null,
    message: newMessage.value,
    eocId: eocId.value,
    // Server echoes this on the newMessage event so this tab can filter out
    // its own echo. Other connections of the same user (other tabs/browsers)
    // still receive and process the event because their connection ID differs.
    senderConnectionId: realtime.connectionId
  };

  messagesModule.addPendingMessage(pendingMsg);
  newMessage.value = '';
  await nextTick();
  scrollToThreadBottom(300);

  attemptSend(clientId, createMessageRequestModel);
}

function handleRetry(message: MessageModel) {
  if (!message._clientId) return;
  messagesModule.updatePendingMessageStatus(message._clientId, 'pending');

  const model = {
    surgeonId: dashboardStore.patient?.surgeonId || 0,
    careTeamId: dashboardStore.patient?.careTeamId || 0,
    dataId: null,
    message: message.message,
    eocId: eocId.value,
    senderConnectionId: realtime.connectionId
  };

  attemptSend(message._clientId, model);
}

function hydrateOooModal() {
  const oooModal = {
    title: outOfOfficeMessageTitle.value,
    template: outOfOfficeMessageDescription.value,
    variant: GeneralPurposeVariant.Information,
  } as GeneralPurposeModalModel;

  generalPurposeModalStore.setModal(oooModal);
}

function setOutOfOfficeModal() {
  if (!messagesModule.hasShownOooModal && messagesModule.outOfOfficeMessageDescription.length) {
    hydrateOooModal();
    messagesModule.setHasShownOooModal();
  }
}

function triggerOooModal() {
  if (setOooMsgMore.value) {
    hydrateOooModal();
  }
}

function scrollToThreadBottom(scrollDuration = 0) {
  const scrollTarget = threadScrollArea.value?.getScrollTarget();
  threadScrollArea.value?.setScrollPosition('vertical', scrollTarget?.scrollHeight ?? 0, scrollDuration);
  markMessagesAsRead();
  scrollToThreadBottomPending.value = false;
}

bus.on('on-bot-resolved', reloadMessages);

</script>

<template>
<div class="dash-msgs">
  <!-- Heading -->
  <div class="row bs-gutter items-center dash-msgs__heading">

    <!-- Care Team  -->
    <div class="col-auto team lt-md">

      <!-- Button -->
      <zbm-skeleton-loader height="26px" :loading="isLoading" width="33px">
        <zbm-btn-icon @click="showCareTeamModal = true" icon="fa-solid fa-users" size="26px" text-color="dark-teal" />
      </zbm-skeleton-loader>

      <!-- Dialog -->
      <zbm-dialog v-model="showCareTeamModal" :close-button="true">

        <!-- Body -->
        <div class="row q-dialog__body">
          <div class="col">
              <TheYourCareTeam v-if="!hasMAOnboardingInterrupt"/>
          </div>
        </div>

        <!-- Footer (CTA button(s)) -->
        <div class="row q-dialog__footer">
          <div class="col-auto">
            <zbm-btn @click="showCareTeamModal = false" :color="$zb.enums.ButtonStdColorEnum.accentText"
                     :height="$zb.enums.ButtonHeightEnum.lg" :label="t('gen_okay')" />
          </div>
        </div>
      </zbm-dialog>
    </div>

    <!-- Title -->
    <div class="col title">
      <zbm-skeleton-loader height="34px" :loading="isLoading" width="100%">
        <h1>{{ t('pwe_messages') }}</h1>
      </zbm-skeleton-loader>
    </div>

    <!-- Disclaimer -->
    <div class="col-auto disclaimer">

      <!-- Button -->
      <zbm-skeleton-loader height="26px" :loading="isLoading" width="26px">
        <zbm-btn-icon @click="showMessagesDisclaimer = true" icon="fa-light fa-circle-question" size="26px"
          text-color="dark-teal" data-testid="disclaimerOkay"/>
      </zbm-skeleton-loader>

      <!-- Modal -->
      <DisclaimerModal ref="disclaimerRef" v-model="showMessagesDisclaimer" disclaimer-id="messagesDisclaimer" opt-out
                       :policy-type="$zb.enums.UserPolicyType.UserPolicyMessagingDisclaimer" :show-on-activate="true"
                       title-key="pwe_pme_messages_header" />
    </div>
    <div class="flex-br" />

    <!-- Date -->
    <div class="col-auto date">
      <zbm-skeleton-loader height="24px" :loading="isLoading" width="100px">
        {{ todaysDate }}
      </zbm-skeleton-loader>
    </div>
  </div>

  <!-- Out of Office Message Banner -->
  <template v-if="!messagesDisabled && outOfOfficeMessageDescription">
    <zbm-skeleton-loader class="mb-30" height="107px" :loading="isLoading" width="100%">
      <q-banner @click="triggerOooModal" :class="['bg-accent', { 'cursor-pointer non-selectable': setOooMsgMore }]"
                inline-actions rounded>

        <!-- Icon -->
        <template #avatar>
          <q-icon name="fa-solid fa-info-circle" size="28px" />
        </template>

        <!-- Content -->
        <template #default>

          <!-- Title -->
          <p v-html="outOfOfficeMessageTitle" class="text-weight-bold mb-none" />

          <!-- More Ellipsis -->
          <q-icon v-if="setOooMsgMore" name="fa-regular fa-ellipsis" />

          <!-- Message -->
          <div class="message">
            <p v-html="outOfOfficeMessageDescription" ref="oooMessage" />
          </div>
        </template>

        <!-- Action(s) -->
        <template #action>
          <zbm-btn-icon v-if="setOooMsgMore" icon="fa-regular fa-angle-right" size="30px" />
        </template>
      </q-banner>
    </zbm-skeleton-loader>
  </template>

  <!-- Thread -->
  <div class="row mb-50 dash-msgs__thread" ref="frame" data-testid="messages">
    <div class="col">
      <zbm-skeleton-loader height="100%" :loading="isLoading" width="100%">

        <!-- No Messages -->
        <p v-if="hideMessagesInput" v-html="noMessages" class="text-italic" data-testid="noMessages" />

        <!-- Messages -->
        <q-scroll-area v-else ref="threadScrollArea" data-testid="scrollArea">
          <MessageDisplay v-for="message in messages"
                          :isClinicianMsg="message.fromId !== currentUserId"
                          :isAssistantMsg = "isAIMsg(message)"
                          :maEnabled="maEnabled"
                          :key="message._clientId || message.messageId"
                          :message="message"
                          :onBotResolved="reloadMessages"
                          :onRetry="handleRetry"
                          data-testid="message"/>
        </q-scroll-area>
      </zbm-skeleton-loader>
    </div>
  </div>

  <!-- Create New Message -->
  <div class="row flex-center dash-msgs dash-msgs__create">

    <!-- Input -->
    <div class="col" data-testid="newMessage">
      <zbm-skeleton-loader height="40px" :loading="isLoading" width="100%">
        <zbm-input-with-validation v-model="newMessage" :disable="messagesDisabled"
                                   :input-width="$zb.enums.FormElWidthEnum.qFormElFull" outlined
                                   :placeholder="t('messages_messenger_placeholder_text_ios')" />
      </zbm-skeleton-loader>
    </div>
    <div class="flex-br mb-10" />

    <!-- Send Button -->
    <div class="col-auto">
      <zbm-skeleton-loader height="40px" :loading="isLoading" width="116px">
        <zbm-btn @click="onSendClick" :color="$zb.enums.ButtonStdColorEnum.accent"
                 :disable="messagesDisabled || !newMessage" :height="$zb.enums.ButtonHeightEnum.md"
                 :label="t('messages_send')" :data-testid="`sendButton`" />
      </zbm-skeleton-loader>
    </div>
  </div>
</div>

</template>

<style lang="scss" scoped>
.dash-msgs {

  // Heading
  &__heading {
    // .row
    margin-block-end: 25px;
  }

  // Thread
  &__thread {
    // .row

    .q-scrollarea {
      height: 70vh;
      padding-inline-end: 20px;
    }

    // Quasar renders the scroll body as an absolutely-positioned, shrink-to-fit
    // element, so without this cap a wide message would let the thread scroll
    // horizontally. The thread must only ever scroll vertically. The % resolves
    // against the (definite) scroll container width.
    :deep(.q-scrollarea__content) {
      max-width: 100%;
    }
  }

  @media (max-width: 499px) {

    // Heading
    &__heading {
      // .row

      // Title
      .title {
        // [class*="col"]
        padding-inline: 0;
      }
    }
  }

  @media (min-width: 500px) {

    // Heading
    &__heading {
      // .row

      // Care Team
      .team {
        // [class*="col"]
        order: 1;
      }

      // Title
      .title {
        // [class*="col"]
        order: 2;
      }

      // Disclaimer
      .disclaimer {
        // [class*="col"]
        order: 4;
      }

      // Date
      .date {
        // [class*="col"]
        order: 3;
      }
    }

    // Create New Message
    &__create {
      // .row

      >[class*="col"] {

        &:first-child {
          padding-inline-end: 5px;
        }

        &:last-child {
          padding-inline-start: 5px;
        }
      }

      .flex-br {
        display: none;
      }
    }
  }

  @media (min-width: $breakpoint-md-min) {}
}

// Out of Office Message
.q-banner {
  padding: 15px 10px 20px 22px;

  :deep(a) {
    color: $pain-rate-ring-bkgd-clr;
    text-decoration: none;
  }

  // Avatar
  :deep(.q-banner__avatar) {
    align-self: flex-start;
  }

  // Content
  :deep(.q-banner__content) {
    position: relative;

    // More Ellipsis
    .q-icon {
      position: absolute;
      bottom: -1px;
      right: 0;
      background-image: linear-gradient(to right, rgba($accent, 0) 0%, rgba($accent, 1) 25%);
      padding-block-start: 9px;
      padding-inline-start: 20px;
    }

    .message {
      // <p>
      max-height: 48px;
      word-break: break-word;
      overflow: hidden;
    }

    // Action(s)
    &+.q-banner__actions.col-auto:not(:empty) {
      padding-inline-start: 15px;
    }
  }
}
</style>
